/*
 * @Author: Alex
 * @LastEditors: Alex yxfacw@163.com
 * @Date: 2023-05-30 19:51:42
 * @Description:
 */
import { Fill, RegularShape, Stroke, Style } from 'ol/style'
import { Color } from 'ol/color'
import { ColorLike } from 'ol/colorlike'
import Map from 'ol/Map'

const DEGREE_UNIT = Math.PI / 180

/**
 * 正方形样式
 * @param radius   正方形边长
 * @param strokeColor stroke color
 * @param strokeWidth  stroke width
 * @param fillColor  fill color
 * @returns style
 */
export function squareShapeStyle(
  radius = 10,
  strokeColor: Color | ColorLike = 'black',
  strokeWidth = 2,
  fillColor: Color | ColorLike = 'red'
): Style {
  return new Style({
    image: new RegularShape({
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({ color: strokeColor, width: strokeWidth }),
      points: 4,
      radius: radius,
      angle: Math.PI / 4
    })
  })
}

/**
 * 三角形样式
 * @param radius
 * @param strokeColor
 * @param strokeWidth
 * @param fillColor
 */
export function triangleStyle(
  radius = 10,
  strokeColor: Color | ColorLike = 'black',
  strokeWidth = 2,
  fillColor: Color | ColorLike = 'red'
) {
  new Style({
    image: new RegularShape({
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({ color: strokeColor, width: strokeWidth }),
      points: 3,
      radius: radius,
      rotation: Math.PI / 4,
      angle: 0
    })
  })
}

/**
 * 五角星样式
 * @param radiusOuter
 * @param radiusInner
 * @param strokeColor
 * @param strokeWidth
 * @param fillColor
 */
export function starStyle(
  radiusOuter = 10,
  radiusInner = 4,
  strokeColor: Color | ColorLike = 'black',
  strokeWidth = 2,
  fillColor: Color | ColorLike = 'red'
) {
  new Style({
    image: new RegularShape({
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({ color: strokeColor, width: strokeWidth }),
      points: 5,
      radius: radiusOuter,
      radius2: radiusInner,
      angle: 0
    })
  })
}

/**
 * 十字样式
 * @param radius
 * @param strokeColor
 * @param strokeWidth
 * @param fillColor
 * @returns
 */
export function crossStyle(
  radius = 10,
  strokeColor: Color | ColorLike = 'black',
  strokeWidth = 2,
  fillColor: Color | ColorLike = 'red'
): Style {
  return new Style({
    image: new RegularShape({
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({ color: strokeColor, width: strokeWidth }),
      points: 4,
      radius: radius,
      radius2: 0,
      angle: 0
    })
  })
}

/**
 * 渲染一个 pulsing 的动态点样式(点图层)
 * @param size canvas 大小
 * @param duration 动画持续时间 值越小 越快
 * @param map  作用的地图实例
 * @returns style
 */
export function pulsingStyle(size = 50, duration = 2000, map: Map): Style {
  return new Style({
    renderer: (pixelCoordinates, state) => {
      const context = state.context
      // 中心点
      const x = pixelCoordinates[0] as number
      const y = pixelCoordinates[1] as number
      context.save()

      const t = (performance.now() % duration) / duration
      const radius = (size / 2) * 0.24
      const outerRadius = (size / 2) * 0.7 * t + radius
      // draw outer circle
      context.clearRect(0, 0, size, size)
      context.beginPath()
      context.arc(x, y, outerRadius, 0, Math.PI * 2)
      context.fillStyle = `rgba(240, 130, 130, 0.02)`
      context.fill()
      // draw inner circle
      context.beginPath()
      context.arc(x, y, radius, 0, Math.PI * 2)
      context.fillStyle = 'rgba(255, 100, 100, 1)'
      context.strokeStyle = 'rgba(255,255,255,0.8)'
      context.lineWidth = 2 + 4 * (1 - t)
      context.fill()
      context.stroke()

      // restore
      context.restore()
      map.render()
    }
  })
}

/**
 * 绘制一个雷达图范围（点图层）
 * @param size 半径
 * @returns
 */
export function radarStyle(size = 100, map: Map): Style {
  const strokeColor = function (alpha: number) {
    return `rgba(111,245,132,${alpha})`
  }
  let time = 0

  return new Style({
    renderer: (pixelCoordinates, state) => {
      // 通过缩放级别重设size值
      const zoom = map.getView().getZoom() ?? 14
      const scale = map.getView().getResolutionForZoom(zoom) * 72 * 32.3701
      const radius = size / (scale * 5)
      // const geometry = state.geometry.clone() as Point
      // geometry.setCoordinates(pixelCoordinates)
      // const extent = geometry.getExtent()
      // const center = getCenter(extent)
      const context = state.context
      const x = pixelCoordinates[0] as number
      const y = pixelCoordinates[1] as number
      context.save()

      context.clearRect(0, 0, radius, radius)
      context.beginPath()
      // 画4个同心圆
      for (let r = radius / 4; r <= radius; r += radius / 4) {
        context.arc(x, y, r, 0, Math.PI * 2)
      }
      context.fillStyle = 'rgba(47,79,79,0.3)'
      context.strokeStyle = strokeColor(0.2)
      context.fill()

      // 画十字线
      context.moveTo(x - radius, y)
      context.lineTo(x + radius, y)
      context.moveTo(x, y - radius)
      context.lineTo(x, y + radius)
      context.stroke()

      // 画扫描的指针
      time += 1
      const lineDeg = (time / 1) % 360
      const lineDegLen = 100

      for (let i = 0; i < lineDegLen; i++) {
        // var deg = (time-i);
        const deg1 = lineDeg - i - 1
        const deg2 = lineDeg - i

        const point1 = radarPoint(radius, deg1)
        const point2 = radarPoint(radius, deg2)
        let opacity = 1 - i / lineDegLen - 0.3
        if (i === 0) opacity = 0.5
        context.beginPath()
        context.fillStyle = strokeColor(opacity)
        context.moveTo(x, y)
        context.lineTo(x + point1.x, y + point1.y)
        context.lineTo(x + point2.x, y + point2.y)
        // ctx.stroke();
        context.fill()
      }
      // restore
      context.restore()
      map.renderSync()
    }
  })
}

function radarPoint(radius: number, degree: number): { x: number; y: number } {
  return {
    x: radius * Math.cos(DEGREE_UNIT * degree),
    y: radius * Math.sin(DEGREE_UNIT * degree)
  }
}
